import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Star, MapPin, Languages, Loader2, Camera, Car, Compass } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { v: "all", l: "All", icon: Compass },
  { v: "Photographer", l: "Photographers", icon: Camera },
  { v: "Local Guide", l: "Guides", icon: Compass },
  { v: "Driver", l: "Drivers", icon: Car },
];

export default function Services() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ date: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });

  useEffect(() => {
    api.get("/data/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, contact_name: user.name || "", contact_email: user.email || "" }));
  }, [user]);

  const shown = filter === "all" ? services : services.filter((s) => s.type === filter);

  const openBooking = (s) => {
    if (!user) {
      toast.info("Please sign in to book a local.");
      navigate("/login");
      return;
    }
    setSelected(s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        service_id: selected.id,
        service_name: selected.name,
        service_type: selected.type,
        ...form,
      });
      toast.success(`Booking request sent to ${selected.name}!`);
      setSelected(null);
      setForm((f) => ({ ...f, date: "", notes: "" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
      <p className="overline text-primary">Trusted local services</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">Book vetted locals</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Photographers, guides and drivers — real people, fair prices, unforgettable experiences.
      </p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="service-filters">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`filter-${f.v}`}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
              filter === f.v ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
            }`}
          >
            <f.icon className="h-4 w-4" /> {f.l}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
        {shown.map((s) => (
          <div key={s.id} className="group overflow-hidden rounded-2xl border bg-card transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
            <div className="relative h-48 overflow-hidden">
              <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute left-3 top-3 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                {s.type}
              </span>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                <span className="inline-flex items-center gap-1 text-sm font-medium">
                  <Star className="h-4 w-4 fill-accent text-accent" /> {s.rating}
                </span>
              </div>
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {s.city} · {s.reviews} reviews
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.bio}</p>
              <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                <Languages className="h-3.5 w-3.5" /> {s.languages.join(", ")}
              </p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-semibold text-primary">{s.price_from}</span>
                <Button size="sm" className="rounded-full" onClick={() => openBooking(s)} data-testid={`book-${s.id}`}>
                  Book now
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Book {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{selected?.type} · {selected?.city} · {selected?.price_from}</p>
            <div className="space-y-2">
              <Label htmlFor="b-date">Preferred date</Label>
              <Input id="b-date" type="date" data-testid="booking-date" value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="b-name">Name</Label>
                <Input id="b-name" data-testid="booking-name" value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="b-phone">Phone</Label>
                <Input id="b-phone" data-testid="booking-phone" value={form.contact_phone}
                  onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-email">Email</Label>
              <Input id="b-email" type="email" data-testid="booking-email" value={form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-notes">Notes</Label>
              <Textarea id="b-notes" data-testid="booking-notes" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Group size, meeting point, special requests…" className="min-h-[70px] rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={submitting || !form.contact_name || !form.contact_email}
              className="w-full rounded-full" data-testid="confirm-booking">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send booking request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
